import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { localServicePages } from '../data/localPages';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === '/';

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false);
    if (!onHome) {
      navigate('/', { state: { scrollTo: sectionId } });
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const goTo = (item) => {
    if (item.path) {
      setIsMobileMenuOpen(false);
      navigate(item.path);
      return;
    }
    scrollToSection(item.id);
  };

  const navItems = [
    { label: 'Home', id: 'hero' },
    { label: 'Services', id: 'services', path: '/services' },
    { label: 'Get a Quote', id: 'quote', path: '/quote' },
    { label: 'Portfolio', id: 'portfolio' },
    { label: 'Case Studies', id: 'case-studies' },
    { label: 'Reviews', id: 'testimonials' },
    { label: 'Contact', id: 'contact' }
  ];

  const localNavItems = [
    ...localServicePages.map((p) => ({ label: p.navLabel, path: `/local/${p.slug}`, slug: p.slug })),
    { label: 'FAQ', path: '/faq', slug: 'faq' },
  ];
  const isLocalActive = localNavItems.some((i) => i.path === location.pathname);

  const headerBg = isScrolled || !onHome ? 'bg-white shadow-md' : 'bg-transparent';

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20 md:h-24 lg:h-28">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <button 
              onClick={() => scrollToSection('hero')}
              className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 hover:opacity-80 transition-opacity"
            >
              <img 
                src="/logo.png" 
                alt="iWurks360 Logo" 
                className="object-contain w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-[120px] lg:h-[120px]"
              />
              <div className="flex flex-col items-start">
                <span className="font-bold text-gray-900 leading-tight text-sm sm:text-base md:text-lg lg:text-xl" style={{ fontFamily: 'var(--font-serif)' }}>
                  iWurks360Digital
                </span>
                <span className="font-bold text-gray-900 leading-tight text-sm sm:text-base md:text-lg lg:text-xl" style={{ fontFamily: 'var(--font-serif)' }}>
                  Solutions
                </span>
              </div>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4 xl:space-x-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => goTo(item)}
                className={`whitespace-nowrap hover:text-gray-900 transition-colors font-medium text-sm xl:text-base ${
                  item.path && location.pathname === item.path ? 'font-bold' : 'text-gray-700'
                }`}
                style={item.path && location.pathname === item.path ? { color: '#D97904' } : undefined}
              >
                {item.label}
              </button>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="nav-local-services"
                className={`flex items-center gap-1 whitespace-nowrap hover:text-gray-900 transition-colors font-medium text-sm xl:text-base outline-none ${
                  isLocalActive ? 'font-bold' : 'text-gray-700'
                }`}
                style={isLocalActive ? { color: '#D97904' } : undefined}
              >
                Local Services <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                {localNavItems.map((item) => (
                  <DropdownMenuItem key={item.path} asChild>
                    <Link to={item.path} data-testid={`nav-local-${item.slug}`} className="cursor-pointer font-medium">
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:block">
            <Button 
              onClick={() => scrollToSection('contact')}
              style={{ backgroundColor: '#D97904' }}
              className="text-white hover:opacity-90 transition-all duration-200 text-sm lg:text-base px-4 py-2 lg:px-6 lg:py-3"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-gray-900 hover:text-gray-700 transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-6 space-y-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                data-testid={`mobile-nav-${item.id}`}
                onClick={() => goTo(item)}
                className="block w-full text-left text-gray-700 hover:text-gray-900 transition-colors font-medium py-2"
              >
                {item.label}
              </button>
            ))}
            {localNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-local-${item.slug}`}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-left text-gray-700 hover:text-gray-900 transition-colors font-medium py-2"
              >
                {item.label}
              </Link>
            ))}
            <Button 
              onClick={() => scrollToSection('contact')}
              style={{ backgroundColor: '#D97904' }}
              className="w-full text-white hover:opacity-90 transition-all duration-200"
            >
              Get Started
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
